import { Box, Stack, Typography, alpha } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import type { ClimateMetricStatesSummary } from '../../../hooks/climateData/useClimateMetricStates'
import { MetricState } from './index'
import MetricStateIndicator from './MetricStateIndicator'

type ClimateMetricStateNoticeProps = {
  summary: ClimateMetricStatesSummary
  /** Noun for the counted unit, e.g. "metric" (property) or "result" (portfolio). */
  unit?: string
  sx?: SxProps<Theme>
}

function pluralize(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'}`
}

/**
 * Compact, single-line banner summarizing non-succeeded climate metric states
 * for the page. Renders nothing when everything has succeeded.
 */
const ClimateMetricStateNotice = ({ summary, unit = 'metric', sx }: ClimateMetricStateNoticeProps) => {
  if (!summary.hasIssues) return null

  const segments: string[] = []
  if (summary.activeCount > 0) segments.push(`${pluralize(summary.activeCount, unit)} calculating`)
  if (summary.failedCount > 0) segments.push(`${summary.failedCount} failed`)
  if (summary.noDataCount > 0) segments.push(`${summary.noDataCount} with no data`)
  if (summary.noRunCount > 0) segments.push(`${summary.noRunCount} not yet processed`)

  const isError = summary.failedCount > 0
  const isActive = summary.activeCount > 0
  const tone: 'error' | 'info' = isError ? 'error' : 'info'

  const indicatorState = isActive
    ? MetricState.RUNNING
    : isError
    ? MetricState.FAILED
    : summary.noDataCount > 0
    ? MetricState.NO_DATA
    : MetricState.NO_RUN

  const headline = isActive
    ? 'Some metrics are still being calculated'
    : isError
    ? 'Some metrics could not be calculated'
    : 'Some metrics are unavailable'

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={[
        theme => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: 1,
          color: `${tone}.main`,
          backgroundColor: alpha(theme.palette[tone].main, 0.08),
          border: `1px solid ${alpha(theme.palette[tone].main, 0.24)}`,
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}>
      <Box sx={{ display: 'flex', flexShrink: 0 }}>
        <MetricStateIndicator state={indicatorState} variant="inline" showLabel={false} sx={{ color: 'inherit' }} />
      </Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 0, sm: 1 }}
        alignItems={{ xs: 'flex-start', sm: 'baseline' }}
        sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} sx={{ color: 'inherit' }}>
          {headline}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {segments.join(' · ')}
        </Typography>
      </Stack>
    </Box>
  )
}

export default ClimateMetricStateNotice
